import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { ApiError } from '../../lib/api';
import { getAdminPlayByDay, type PlayByDay } from '../../lib/api/admin';
import { buildSeries, buildViews, xLabelsFor, type ChartPage, type ResolvedView } from '../../lib/playViews';
import { LineChart } from './LineChart';

/**
 * Graph view of the Gameplay card (GHS5-99): day-by-day Runs and Players, one
 * line per promotion week.
 *
 * Read-only. Everything here is a regrouping of rows the game already writes —
 * see api/src/modules/admin/admin.playByDay.service.ts.
 *
 * Deliberately NOT scoped by the Overview's date-range selector. These windows
 * are the promotion weeks themselves; a date range would cut the lines mid-week
 * and make weeks incomparable. The Table view remains the range-filtered
 * readout.
 *
 * Mounted only when the operator switches to Graph, so the fetch never fires
 * for admins who stay on the table.
 */

type State =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; data: PlayByDay };

export function GameplayCharts() {
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [viewId, setViewId] = useState<string | null>(null);
  // null means "the newest page" — a sentinel so switching views lands on the
  // most recent window without recomputing page counts up here.
  const [page, setPage] = useState<number | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    getAdminPlayByDay({ signal: controller.signal })
      .then((data) => setState({ kind: 'ready', data }))
      .catch((err) => {
        if (controller.signal.aborted) return;
        setState({
          kind: 'error',
          message:
            err instanceof ApiError
              ? `Couldn't load daily play (${err.status}).`
              : "Couldn't load daily play. Please try again.",
        });
      });
    return () => controller.abort();
  }, []);

  const views = useMemo(() => (state.kind === 'ready' ? buildViews(state.data) : []), [state]);

  if (state.kind === 'loading') return <p className="text-white/50">Loading daily play…</p>;
  if (state.kind === 'error') return <p className="text-white/80">{state.message}</p>;
  if (views.length === 0) return <p className="text-white/50">No weeks scheduled.</p>;

  const view = views.find((v) => v.id === viewId) ?? views[0]!;
  const pageCount = view.pages.length;
  const pageIndex = Math.min(pageCount - 1, page ?? pageCount - 1);
  const activePage = view.pages[pageIndex];

  const selectView = (id: string): void => {
    setViewId(id);
    setPage(null);
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs uppercase tracking-wide text-white/55">View</span>
        {views.map((v) => (
          <button
            key={v.id}
            type="button"
            aria-pressed={v.id === view.id}
            onClick={() => selectView(v.id)}
            className={[
              'rounded-md border px-3 py-1.5 text-sm transition focus:outline-none focus-visible:ring-2 focus-visible:ring-white',
              v.id === view.id
                ? 'border-brand-redAccent bg-brand-redAccent/20 text-white'
                : 'border-white/15 text-white/75 hover:bg-white/10',
            ].join(' ')}
          >
            {v.label}
          </button>
        ))}
      </div>

      {!activePage ? (
        <p className="text-white/50">{view.label} hasn&rsquo;t started yet — nothing to chart.</p>
      ) : (
        <>
          {pageCount > 1 && (
            <Pager
              page={pageIndex}
              pageCount={pageCount}
              label={activePage.label}
              onChange={setPage}
            />
          )}
          <MetricChart view={view} page={activePage} metric="runs" title="Runs per day" />
          <MetricChart
            view={view}
            page={activePage}
            metric="players"
            title="Players per day"
            note="Daily actives — a player who plays on two days counts on both, so these sum higher than the week's unique-player total above."
          />
        </>
      )}

      <p className="text-[11px] text-white/40">
        Days run Friday to Thursday in {state.data.timezone}, matching the promotion period. This
        graph covers whole weeks and is not affected by the date range above.
      </p>
    </div>
  );
}

function MetricChart({
  view,
  page,
  metric,
  title,
  note,
}: {
  view: ResolvedView;
  page: ChartPage;
  metric: 'runs' | 'players';
  title: string;
  note?: string;
}) {
  return (
    <div className="rounded-lg border border-white/10 bg-black/20 p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h4 className="font-heading text-xs uppercase tracking-wide text-white/55">{title}</h4>
        {view.kind === 'overlay' && (
          <span className="text-[11px] text-white/40">{page.label}</span>
        )}
      </div>
      <div className="mt-3">
        <LineChart
          series={buildSeries(view, page, metric)}
          xLabels={xLabelsFor(view, page)}
          valueLabel={metric}
          ariaLabel={`${title}, ${view.label}, ${page.label}`}
        />
      </div>
      {note && <Note>{note}</Note>}
    </div>
  );
}

function Note({ children }: { children: ReactNode }) {
  return <p className="mt-3 text-[11px] text-white/40">{children}</p>;
}

function Pager({
  page,
  pageCount,
  label,
  onChange,
}: {
  page: number;
  pageCount: number;
  label: string;
  onChange: (page: number) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <PagerButton label="Earlier" disabled={page === 0} onClick={() => onChange(page - 1)} />
      <span className="text-sm text-white/70">
        {label}
        <span className="ml-2 text-white/40">
          ({page + 1} of {pageCount})
        </span>
      </span>
      <PagerButton
        label="Later"
        disabled={page === pageCount - 1}
        onClick={() => onChange(page + 1)}
      />
    </div>
  );
}

function PagerButton({
  label,
  disabled,
  onClick,
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="rounded-md border border-white/15 px-3 py-1.5 text-sm text-white/75 transition hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white disabled:cursor-not-allowed disabled:opacity-35 disabled:hover:bg-transparent"
    >
      {label}
    </button>
  );
}
